import { useCallback, useEffect, useState } from "react"
import { Box, CloseButton, Dialog, HStack, Portal, Stack, VStack } from "@chakra-ui/react"
import { Button, Text } from "@/components"
import {
  listPlaidItemAccounts,
  selectPlaidItemAccount,
  getAccountChangeCheck,
  AccountChangeCheck,
  PlaidItemAccount,
  PaymentsApiError,
} from "@/api/payments"
import { ACCOUNT_IN_PROCESS_TEXT, formatChangeAmount } from "@/features/compliance/components"

// "Which account receives your payments?" — a Plaid connection (item) often
// exposes several accounts even though the client picked just one in Plaid
// Link. This lists everything on the connected item (labels + masks only) and
// lets the client re-point the payment target without a new Link session.
// Renders nothing unless the item actually has 2+ accounts. Client-only: the
// parent hides it in DM view-as mode, and the backend enforces the same.

interface Props {
  paymentsClientId: string
  dealEsoId?: number
  // A payment is in flight. Only the fallback lock, used when changeCheck is
  // unavailable: the history cannot tell a payment waiting for approval (which a switch
  // cancels and resubmits) from an approved one (which blocks it).
  hasPendingPayment: boolean
  // The payments server's answer (Bill, 2026-10-13): `in_process` (approved or on its
  // way) locks switching until it is released or cancelled; `waiting` is cancelled and
  // sent again for approval to the new account once the client confirms in the dialog.
  changeCheck?: AccountChangeCheck | null
  // Parent refresh (bank list + history) after a successful switch.
  onSwitched: () => Promise<void> | void
}

const titleCase = (s: string) => s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())

const accountLabel = (a: PlaidItemAccount) => `${a.name}${a.mask ? ` ••${a.mask}` : ""}`

// Radio indicator drawn to match the portal's green accent; Chakra's RadioGroup
// isn't used because rows are whole-card click targets with mixed disabled
// semantics (unpayable vs in-flight) that need custom copy per state.
const RadioDot = ({ selected }: { selected: boolean }) => (
  <Box
    flexShrink={0}
    w="16px"
    h="16px"
    borderRadius="full"
    borderWidth={selected ? "5px" : "2px"}
    borderColor={selected ? "green.500" : "gray.300"}
    bg="white"
    transition="border-color 0.15s, border-width 0.15s"
  />
)

export const BankAccountSelector = ({ paymentsClientId, dealEsoId, hasPendingPayment, changeCheck, onSwitched }: Props) => {
  const [accounts, setAccounts] = useState<PlaidItemAccount[]>([])
  const [confirmTarget, setConfirmTarget] = useState<PlaidItemAccount | null>(null)
  const [isSwitching, setIsSwitching] = useState(false)
  const [errorText, setErrorText] = useState("")
  const [switchedText, setSwitchedText] = useState("")
  // The payment waiting for approval this switch cancels and resubmits, shown in the dialog.
  const [waitingChange, setWaitingChange] = useState<{ amount: number; currency?: string } | null>(null)

  const fetchAccounts = useCallback(async () => {
    try {
      const result = await listPlaidItemAccounts(paymentsClientId)
      setAccounts(result.accounts ?? [])
    } catch {
      setAccounts([]) // selector simply doesn't render — connect/disconnect still work
    }
  }, [paymentsClientId])

  useEffect(() => { void fetchAccounts() }, [fetchAccounts])

  const handleConfirm = useCallback(async () => {
    if (!confirmTarget) return
    setIsSwitching(true)
    setErrorText("")
    setSwitchedText("")
    try {
      const result = await selectPlaidItemAccount({
        client_id: paymentsClientId,
        plaid_account_id: confirmTarget.plaid_account_id,
        deal_eso_id: dealEsoId,
        confirm_cancel_pending: waitingChange ? true : undefined,
      })
      setSwitchedText(`Payments will now go to ${accountLabel(confirmTarget)}.` + (result?.cancelled_transaction
        ? " Your payment that was waiting for approval has been sent again for approval and will be paid to this account."
        : ""))
      setConfirmTarget(null)
      setWaitingChange(null)
      await fetchAccounts()
      await onSwitched()
    } catch (err) {
      // A payment started waiting for approval after the dialog opened: show it in the
      // dialog and let the client confirm, rather than a dead-end error.
      if (err instanceof PaymentsApiError && err.code === "CONFIRM_CANCEL_PENDING") {
        const again = await getAccountChangeCheck(paymentsClientId)
        if (again?.waiting) {
          setWaitingChange(again.waiting)
          return
        }
      }
      setErrorText(err instanceof PaymentsApiError ? err.message : "We couldn't switch accounts. Please try again.")
      setConfirmTarget(null)
      setWaitingChange(null)
    } finally {
      setIsSwitching(false)
    }
  }, [confirmTarget, paymentsClientId, dealEsoId, fetchAccounts, onSwitched, waitingChange])

  if (accounts.length < 2) return null

  // The server's answer when there is one; otherwise any payment in flight locks.
  const changeLocked = changeCheck ? !!changeCheck.in_process : hasPendingPayment
  const locked = changeLocked || isSwitching

  const pick = async (acct: PlaidItemAccount) => {
    if (locked || acct.is_current || !acct.payable) return
    setErrorText("")
    setSwitchedText("")
    // Asked again now, so the dialog says whether a waiting payment is cancelled.
    const fresh = (await getAccountChangeCheck(paymentsClientId)) ?? changeCheck ?? null
    if (fresh?.in_process) {
      setErrorText(ACCOUNT_IN_PROCESS_TEXT)
      return
    }
    setWaitingChange(fresh?.waiting ?? null)
    setConfirmTarget(acct)
  }

  return (
    <Stack mt={3} pt={3} borderTopWidth="1px" borderColor="green.200" gap={2}>
      <HStack justifyContent="space-between" alignItems="baseline">
        <Text fontSize="xs" fontWeight="bold" color="gray.700" textTransform="uppercase" letterSpacing="0.05em">
          Payout Account
        </Text>
        <Text fontSize="xs" color="gray.500">
          {accounts.length} accounts shared by your bank
        </Text>
      </HStack>
      <Stack gap={2} role="radiogroup" aria-label="Account that receives your payments">
        {accounts.map((acct) => {
          const clickable = !locked && !acct.is_current && acct.payable
          return (
            <HStack
              key={acct.plaid_account_id}
              role="radio"
              aria-checked={acct.is_current}
              aria-disabled={!clickable && !acct.is_current}
              tabIndex={clickable ? 0 : -1}
              onKeyDown={(e) => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); void pick(acct) } }}
              onClick={() => { void pick(acct) }}
              p={3}
              gap={3}
              borderWidth="1px"
              borderRadius="md"
              borderColor={acct.is_current ? "green.500" : "gray.200"}
              boxShadow={acct.is_current ? "0 0 0 1px var(--chakra-colors-green-500)" : "none"}
              bg="white"
              opacity={acct.payable ? (locked && !acct.is_current ? 0.6 : 1) : 0.55}
              cursor={clickable ? "pointer" : "default"}
              transition="border-color 0.15s, box-shadow 0.15s"
              _hover={clickable ? { borderColor: "green.400" } : undefined}
              _focusVisible={{ outline: "2px solid var(--chakra-colors-green-500)", outlineOffset: "1px" }}
              alignItems="center"
            >
              <RadioDot selected={!!acct.is_current} />
              <Stack gap={0} flex={1} minW={0}>
                <Text fontSize="sm" fontWeight="medium" color="gray.800" lineClamp={1}>
                  {accountLabel(acct)}
                </Text>
                {acct.account_type && (
                  <Text fontSize="xs" color="gray.500">{titleCase(acct.account_type)}</Text>
                )}
              </Stack>
              {acct.is_current ? (
                <Box bg="green.100" borderRadius="full" px={2.5} py={0.5} flexShrink={0}>
                  <Text fontSize="xs" fontWeight="semibold" color="green.700" whiteSpace="nowrap">
                    Receiving payments
                  </Text>
                </Box>
              ) : !acct.payable ? (
                <Text fontSize="xs" color="gray.400" flexShrink={0} title="Your bank didn't provide transfer numbers for this account">
                  Unavailable
                </Text>
              ) : null}
            </HStack>
          )
        })}
      </Stack>
      {changeLocked && (
        <Text fontSize="xs" color="gray.500">
          A payment is on its way to your selected account — you can switch once it&rsquo;s released.
        </Text>
      )}
      {switchedText && (
        <Text fontSize="xs" fontWeight="medium" color="green.700">✓ {switchedText}</Text>
      )}
      {errorText && (
        <Text fontSize="xs" fontWeight="medium" color="red.600">{errorText}</Text>
      )}

      {/* Confirm switch */}
      <Dialog.Root open={!!confirmTarget} onOpenChange={(e) => { if (!e.open && !isSwitching) setConfirmTarget(null) }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <VStack p={10} gap={4}>
                <Text fontSize="2xl" fontWeight="bold" textAlign="center">Change Payout Account?</Text>
                <Text textAlign="center" color="gray.700">
                  Future payments from ESO Fund will be deposited to{" "}
                  <strong>{confirmTarget ? accountLabel(confirmTarget) : ""}</strong>{" "}
                  instead of your currently selected account.
                </Text>
                {waitingChange && (
                  // gray text, not orange: this theme's orange scale is inverted.
                  <Text textAlign="center" fontSize="sm" color="gray.800" p={3} borderWidth="1px" borderRadius="md" borderColor="orange.200" bg="orange.50">
                    Your payment of {formatChangeAmount(waitingChange.amount, waitingChange.currency)} that is waiting for approval
                    will be cancelled and sent again for approval to this account.
                  </Text>
                )}
                <Text textAlign="center" fontSize="sm" color="gray.500">
                  Your equity partner will be notified of this change.
                </Text>
                <HStack mt={3}>
                  {/* `loading`, not `isLoading` — v2's prop is silently ignored by the v3
                      Button, which left this confirm clickable mid-request. Same bug, same
                      day, same fix as the disconnect confirm. */}
                  <Button variant="solid" colorPalette="green" onClick={handleConfirm} loading={isSwitching} disabled={isSwitching}>
                    Yes, Use This Account
                  </Button>
                  <Button variant="outline" onClick={() => setConfirmTarget(null)} disabled={isSwitching}>
                    Cancel
                  </Button>
                </HStack>
              </VStack>
              <Dialog.CloseTrigger asChild position="absolute" top={3} right={3}>
                <CloseButton size="sm" disabled={isSwitching} />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Stack>
  )
}
